import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Shield, Users } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { PEOOrganizationService } from '@/services/peo-organization.service';
import { ROLE_TYPE_LABELS, PERMISSION_LABELS } from '@/types/peo-organization.types';
import { CreateRoleDialog } from './CreateRoleDialog';
import { EditRoleDialog } from './EditRoleDialog';
import { RoleAssignmentDialog } from './RoleAssignmentDialog';

interface OrganizationalRolesManagerProps {
  providerId: string;
  searchTerm: string;
}

export const OrganizationalRolesManager: React.FC<OrganizationalRolesManagerProps> = ({
  providerId,
  searchTerm
}) => {
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);

  const { data: roles, isLoading } = useQuery({
    queryKey: ['organizational-roles', providerId],
    queryFn: () => PEOOrganizationService.getOrganizationalRoles(providerId),
    enabled: !!providerId
  });

  const filteredRoles = roles?.filter(role =>
    role.role_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.description?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Organizational Roles</h2>
          <p className="text-muted-foreground">
            Define and manage roles with specific permissions for your organization.
          </p>
        </div>
        <CreateRoleDialog providerId={providerId}>
          <Button className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Create New Role
          </Button>
        </CreateRoleDialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-5 bg-muted rounded w-3/4"></div>
                <div className="h-4 bg-muted rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="h-4 bg-muted rounded"></div>
                  <div className="h-4 bg-muted rounded w-2/3"></div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : filteredRoles.length === 0 ? (
          <div className="col-span-full">
            <Card>
              <CardContent className="py-8 text-center">
                <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No roles found</p>
                {searchTerm && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Try adjusting your search term
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          filteredRoles.map((role) => (
            <Card 
              key={role.id} 
              className={`hover:shadow-lg transition-shadow cursor-pointer ${
                selectedRoleId === role.id ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => setSelectedRoleId(role.id)}
            >
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    {role.role_name}
                  </span>
                  <Badge variant="secondary">
                    {ROLE_TYPE_LABELS[role.role_type]}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  {role.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Permission Level:</span>
                  <Badge variant="outline">
                    {PERMISSION_LABELS[role.permission_level]}
                  </Badge>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium">Key Permissions:</p>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    {role.can_view_applications && (
                      <Badge variant="outline" className="text-xs">View Apps</Badge>
                    )}
                    {role.can_create_quotes && (
                      <Badge variant="outline" className="text-xs">Create Quotes</Badge>
                    )}
                    {role.can_view_analytics && (
                      <Badge variant="outline" className="text-xs">Analytics</Badge>
                    )}
                    {role.can_manage_team && (
                      <Badge variant="outline" className="text-xs">Manage Team</Badge>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <EditRoleDialog providerId={providerId} role={role}>
                    <Button variant="outline" size="sm" className="flex-1">
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                  </EditRoleDialog>
                  <RoleAssignmentDialog providerId={providerId} roleId={role.id}>
                    <Button variant="outline" size="sm" className="flex-1">
                      <Users className="h-3 w-3 mr-1" />
                      Assign
                    </Button>
                  </RoleAssignmentDialog>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};